import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Link } from "react-router-dom";
import { Sticker } from "./Sticker";

const DIRECTOR_IMG = "https://customer-assets.emergentagent.com/job_shah-edu-3d/artifacts/d3yii1pn_image.png";

export default function Story() {
  return (
    <section id="story" className="grid-bg py-20 md:py-28 relative bg-[#FAFAFA] overflow-hidden" data-testid="story-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        {/* Our Story */}
        <div className="grid lg:grid-cols-12 gap-10 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <Sticker rotate={-4}>★ OUR STORY</Sticker>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.9]">
              Built by a <span className="gradient-text-rb">student</span>,<br />for students.
            </h2>
          </motion.div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -2 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true }}
              whileHover={{ rotate: -1.5, y: -4 }}
              className="bg-white border-2 border-[#282829] rounded-3xl p-6 brutal-shadow-red-sm"
            >
              <p className="text-base leading-relaxed">
                Founded 10+ years ago by American university alumnus <b>Mr. Nikit Shah</b>, Shah Overseas counsels students from Nagpur, Pune & Dubai for world-class education across Ireland, USA, UK, Canada, Europe, Australia, NZ & more.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: 2 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ rotate: 1.5, y: -4 }}
              className="bg-[#282829] text-white border-2 border-[#282829] rounded-3xl p-6 brutal-shadow-red-sm"
            >
              <p className="text-base leading-relaxed">
                We're <b className="text-[#c92e34]">research-driven</b> & ethical — no one-size-fits-all. Our counselling starts with <i>your</i> background, aspirations & career goals, then we build a strategy just for you.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Red banner — bridges 'Our Story' to Director profile · full-bleed on desktop */}
        <div className="my-12 md:my-16 relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
          <div className="relative overflow-hidden bg-[#c92e34] text-white border-y-[3px] border-[#282829]" data-testid="director-banner">
            <div
              className="absolute inset-0 opacity-15 pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(#fff 1.2px, transparent 1.2px)",
                backgroundSize: "22px 22px",
              }}
            />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-10 md:py-16 flex flex-col md:flex-row items-center justify-between gap-6">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 70 }}
                className="flex-1"
              >
                <span className="inline-block bg-white text-[#c92e34] text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full border-2 border-[#282829] -rotate-2">
                  Meet the Founder &amp; Director
                </span>
                <h3
                  className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.9] mt-4 tracking-tight"
                  data-testid="director-tagline-headline"
                >
                  KICKASS{" "}
                  <span className="font-handwritten text-[#282829] inline-block -rotate-3">student</span>
                  <br />
                  BRAND STRATERGIST.
                </h3>
              </motion.div>
              <motion.div
                initial={{ scale: 0, rotate: 0 }}
                whileInView={{ scale: 1, rotate: -8 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 180 }}
                className="hidden md:grid w-32 h-32 lg:w-40 lg:h-40 rounded-full bg-[#282829] border-[3px] border-white place-items-center text-center brutal-shadow-ink flex-shrink-0"
                aria-hidden
              >
                <div>
                  <div className="font-anton text-3xl lg:text-4xl text-[#c92e34] leading-none">10+</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-white/70 mt-1">Years</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-white/70">Of mastery</div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Director Profile */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 60 }}
              whileHover={{ rotate: -1, scale: 1.01 }}
              className="relative aspect-[4/5] rounded-3xl overflow-hidden border-2 border-[#282829] brutal-shadow-red"
            >
              <img
                src={DIRECTOR_IMG}
                alt="Nikit Shah, Director"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#282829]/95 via-[#282829]/40 to-transparent p-5">
                <div className="text-white font-display text-3xl">Nikit Shah</div>
                <div className="text-[#c92e34] text-xs font-bold tracking-widest uppercase">Director · Founder</div>
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-7">
            <Quote className="text-[#c92e34]" size={36} />
            <p className="font-display text-2xl md:text-3xl leading-snug mt-2">
              "From American university classrooms to launching Shah Overseas — my mission is honest, informed guidance for every aspiring student."
            </p>
            <p className="mt-5 text-[#282829]/70 leading-relaxed">
              MS in Electrical Engineering from American University, B.E. from Nagpur University. Worked with <b>top global tech firms — Siemens (Germany) & Intel (USA)</b> before turning his lens on Indian students. Counselled 45,000+ students and helped them earn scholarships worth thousands of dollars. Today, an esteemed advisor to <b>350+ world universities</b>.
            </p>

            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["NAFSA, USA", "EAIE, Europe", "ASCA, USA", "British Council"].map((m, i) => (
                <motion.div
                  key={m}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className={`text-center font-bold text-xs uppercase tracking-wider px-3 py-3 rounded-2xl border-2 border-[#282829] ${
                    i % 2 ? "bg-[#282829] text-white" : "bg-white"
                  }`}
                  data-testid={`director-membership-${i}`}
                >
                  {m}
                </motion.div>
              ))}
            </div>

            <Link
              to="/statistics"
              data-testid="check-my-statistics-btn"
              className="mt-7 inline-flex items-center gap-2 bg-[#c92e34] hover:bg-[#282829] text-white font-bold px-6 py-4 rounded-full border-2 border-[#282829] brutal-shadow-ink transition-colors"
            >
              📊 Check My Statistics <span className="text-base">→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
